import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Global notification-highlight handler.
 *
 * NotificationBell appends `?highlight=<id>&hl_color=<key>` to the URL when
 * the user clicks a notification. This component watches the URL, finds an
 * element with a matching `data-highlight-id`, scrolls it into view, and
 * applies a blinking ring whose colour reflects the notification action
 * (order/invoice/dispatch/credit-note/call-request/etc.).
 *
 * Mount once at the top of <Layout/> — pages don't need to opt-in beyond
 * tagging their primary rows with `data-highlight-id="<id>"`.
 */
const COLOR_CLASSES = {
  order: "blink-emerald",
  invoice: "blink-blue",
  dispatch: "blink-amber",
  credit_note: "blink-rose",
  call_request: "blink-violet",
  client: "blink-pink",
  task: "blink-indigo",
  default: "blink-emerald",
};

export default function NotificationHighlighter() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("highlight");
    const colorKey = params.get("hl_color") || "default";
    if (!id) return;
    const cls = COLOR_CLASSES[colorKey] || COLOR_CLASSES.default;
    let attempts = 0;
    const tick = setInterval(() => {
      attempts += 1;
      // Search by data-highlight-id first, then by data-testid suffix as a
      // fallback so we work with the many existing testid-based rows.
      const target =
        document.querySelector(`[data-highlight-id="${CSS.escape(id)}"]`) ||
        document.querySelector(`[data-testid$="-${CSS.escape(id)}"]`);
      if (target) {
        clearInterval(tick);
        try { target.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
        target.classList.add(cls, "notif-blink");
        setTimeout(() => target.classList.remove(cls, "notif-blink"), 6000);
        // Clean the query params so a refresh doesn't re-trigger.
        const cleaned = new URLSearchParams(location.search);
        cleaned.delete("highlight");
        cleaned.delete("hl_color");
        const next = cleaned.toString();
        navigate({ pathname: location.pathname, search: next ? `?${next}` : "" }, { replace: true });
      }
      if (attempts >= 20) clearInterval(tick); // ~6s timeout
    }, 300);
    return () => clearInterval(tick);
  }, [location.search, location.pathname, navigate]);

  return null;
}
